import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * ResearchTaxonomy — generic CRUD table for categories/tags/authors/templates.
 * Reused via 4 wrapper pages below (one per collection).
 */
export function ResearchTaxonomy({ resource, label, keyField = "slug", fields }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/${resource}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startEdit = (it) => { setForm({ ...it }); setEditing(it[keyField]); };
  const reset = () => { setForm({}); setEditing(null); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/${resource}/${editing}`, form);
      else await api.post(`/admin/${resource}`, form);
      toast.success("Saved"); reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete '${key}'?`)) return;
    await api.delete(`/admin/${resource}/${key}`); load();
  };

  return (
    <div data-testid={`admin-${resource}-page`}>
      <div className="mb-6">
        <div className="eyebrow mb-2">Research Hub</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">{label}</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
              <tr>
                {fields.filter((f) => f.showInTable !== false).map((f) => <th key={f.key} className="text-left px-4 py-2">{f.label}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it[keyField]} className="border-b border-slate-100 last:border-b-0" data-testid={`${resource}-row-${it[keyField]}`}>
                  {fields.filter((f) => f.showInTable !== false).map((f) => <td key={f.key} className="px-4 py-3 text-slate-700 text-sm">{Array.isArray(it[f.key]) ? it[f.key].join(", ") : (it[f.key] || "—").toString().slice(0, 80)}</td>)}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(it)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`${resource}-edit-${it[keyField]}`}><Pencil className="w-3.5 h-3.5 text-slate-500"/></button>
                    <button onClick={() => remove(it[keyField])} className="p-1.5 rounded hover:bg-red-50" data-testid={`${resource}-delete-${it[keyField]}`}><Trash2 className="w-3.5 h-3.5 text-red-500"/></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={fields.length + 1} className="text-center text-slate-500 text-sm py-8">None yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <form onSubmit={save} className="border border-slate-200 rounded-md bg-white p-5 space-y-3 h-fit">
          <div className="font-display text-lg font-semibold">{editing ? `Edit ${editing}` : `New ${label.slice(0, -1)}`}</div>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{f.label}{f.required && " *"}</label>
              {f.type === "textarea" ? (
                <Textarea rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`${resource}-field-${f.key}`}/>
              ) : (
                <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`${resource}-field-${f.key}`} disabled={editing && f.key === keyField}/>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            {editing && <Button type="button" variant="ghost" onClick={reset}>Cancel</Button>}
            <Button type="submit" data-testid={`${resource}-save`} className="bg-blue-600 hover:bg-blue-700 text-white flex-1"><Plus className="w-4 h-4 mr-1"/> {editing ? "Save" : "Create"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AdminCategories() {
  return <ResearchTaxonomy resource="article-categories" label="Categories" fields={[
    { key: "name", label: "Name", required: true },
    { key: "slug", label: "Slug" },
    { key: "description", label: "Description", type: "textarea", showInTable: false },
    { key: "color", label: "Color", showInTable: false },
  ]}/>;
}
export function AdminTags() {
  return <ResearchTaxonomy resource="article-tags" label="Tags" fields={[
    { key: "name", label: "Name", required: true },
    { key: "slug", label: "Slug" },
  ]}/>;
}
export function AdminAuthors() {
  return <ResearchTaxonomy resource="article-authors" label="Authors" fields={[
    { key: "name", label: "Name", required: true },
    { key: "slug", label: "Slug" },
    { key: "title", label: "Title" },
    { key: "bio", label: "Bio", type: "textarea", showInTable: false },
    { key: "avatar", label: "Avatar URL", showInTable: false },
    { key: "email", label: "Email", showInTable: false },
    { key: "linkedin", label: "LinkedIn", showInTable: false },
  ]}/>;
}
export function AdminTemplates() {
  return <ResearchTaxonomy resource="article-templates" label="Templates" keyField="key" fields={[
    { key: "key", label: "Key", required: true },
    { key: "name", label: "Name", required: true },
    { key: "description", label: "Description" },
    { key: "content_html", label: "HTML template", type: "textarea", showInTable: false },
  ]}/>;
}
